import React, { useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Search, Info } from "lucide-react";
import { gstRates, gstSpecialRates } from "../../data/taxData";

const colorMap = {
  emerald: {
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
    border: "border-l-emerald-500",
    head: "text-emerald-600",
    dot: "bg-emerald-500",
  },
  blue: {
    badge: "bg-sky-50 text-sky-700 border-sky-200",
    border: "border-l-sky-500",
    head: "text-sky-600",
    dot: "bg-sky-500",
  },
  indigo: {
    badge: "bg-indigo-50 text-indigo-700 border-indigo-200",
    border: "border-l-indigo-500",
    head: "text-indigo-600",
    dot: "bg-indigo-500",
  },
  red: {
    badge: "bg-red-50 text-red-700 border-red-200",
    border: "border-l-red-500",
    head: "text-red-600",
    dot: "bg-red-500",
  },
};

export const GSTRateSheet = () => {
  const [search, setSearch] = useState("");
  const [activeRate, setActiveRate] = useState("all");

  const filtered = gstRates
    .filter((g) => activeRate === "all" || g.rate === activeRate)
    .map((g) => ({
      ...g,
      items: search ? g.items.filter((i) => i.toLowerCase().includes(search.toLowerCase())) : g.items,
    }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="space-y-6" data-testid="gst-rate-sheet">
      <div className="bg-brand-orange/10 border border-brand-orange/30 rounded-lg p-4 flex gap-3 items-start">
        <Info className="h-5 w-5 text-brand-orange mt-0.5 shrink-0" />
        <p className="text-sm text-brand-navy">
          Latest simplified GST structure (effective 22 September 2025) — a two-slab model of <strong>5%</strong> and <strong>18%</strong>, with <strong>0%</strong> for essentials and a <strong>40%</strong> demerit rate for luxury & sin goods.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
          <Input
            data-testid="gst-search-input"
            placeholder="Search items (e.g., soap, cement, insurance...)"
            className="pl-9 bg-white"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["all", "0%", "5%", "18%", "40%"].map((r) => (
            <button
              key={r}
              data-testid={`gst-filter-${r === "all" ? "all" : r.replace("%", "pct")}`}
              onClick={() => setActiveRate(r)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                activeRate === r
                  ? "bg-brand-orange text-white border-brand-orange"
                  : "bg-white text-brand-navy border-brand-border hover:border-brand-orange"
              }`}
            >
              {r === "all" ? "All Rates" : r}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {filtered.map((g) => {
          const c = colorMap[g.color];
          return (
            <Card key={g.rate} className={`bg-white border border-brand-border border-l-4 ${c.border} shadow-sm`} data-testid={`gst-card-${g.rate.replace("%", "pct")}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-3xl font-bold ${c.head}`}>{g.rate}</span>
                  <Badge variant="outline" className={c.badge}>{g.label}</Badge>
                </div>
                <p className="text-sm text-brand-muted mb-4">{g.description}</p>
                <ul className="space-y-2">
                  {g.items.map((item) => (
                    <li key={item} className="text-sm text-brand-navy flex gap-2">
                      <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${c.dot}`} />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <p className="text-brand-muted col-span-2 text-center py-8" data-testid="gst-no-results">No items found for "{search}"</p>
        )}
      </div>

      <Card className="bg-brand-grey border border-brand-border">
        <CardContent className="p-5">
          <h4 className="font-semibold text-brand-navy mb-3">Special Rates</h4>
          <div className="grid sm:grid-cols-3 gap-3">
            {gstSpecialRates.map((s) => (
              <div key={s.rate} className="bg-white rounded-lg border border-brand-border p-3">
                <span className="text-lg font-bold text-brand-orange">{s.rate}</span>
                <p className="text-sm text-brand-muted mt-1">{s.items}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
